import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { legalContent, legalFooterLabels, LAST_UPDATED } from "../data/legalContent";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mice-latam-pro/artifacts/pdq87uby_cropped-LOGOS-IR-1.png";
const SITE_URL = "https://industriadereuniones.com";

const LegalPage = () => {
  const { slug } = useParams();
  const { t, language, toggleLanguage } = useLanguage();

  const doc = legalContent[slug];

  // Inject BreadcrumbList JSON-LD + canonical + title
  useEffect(() => {
    if (!doc) return;
    const title = `${t(doc.title)} | Industria de Reuniones`;
    document.title = title;

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/legal/${slug}`;

    // BreadcrumbList JSON-LD
    const breadcrumbId = "legal-breadcrumb-jsonld";
    let script = document.getElementById(breadcrumbId);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = breadcrumbId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: language === "es" ? "Inicio" : "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: language === "es" ? "Legal" : "Legal", item: `${SITE_URL}/legal/${slug}` },
        { "@type": "ListItem", position: 3, name: t(doc.title), item: `${SITE_URL}/legal/${slug}` },
      ],
    });

    return () => {
      // Reset canonical to home on unmount
      if (canonical) canonical.href = `${SITE_URL}/`;
      const s = document.getElementById(breadcrumbId);
      if (s) s.remove();
    };
  }, [slug, language, doc, t]);

  if (!doc) return <Navigate to="/" replace />;

  return (
    <div data-testid={`legal-page-${slug}`} className="min-h-screen bg-[#EEF1F4]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#0B1F3B]/10">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#0B1F3B] hover:text-[#F59E0B] transition-colors group">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span className="font-body text-sm font-semibold">{t(legalFooterLabels.backHome)}</span>
          </Link>

          <Link to="/">
            <img src={LOGO_URL} alt="Industria de Reuniones" className="h-8 md:h-10" />
          </Link>

          <button
            onClick={toggleLanguage}
            data-testid="legal-language-toggle"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B1F3B] text-white text-xs font-body font-semibold uppercase tracking-wider hover:bg-[#0B1F3B]/85 transition-colors"
            aria-label="Toggle language"
          >
            {language === "es" ? "EN" : "ES"}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs uppercase tracking-[0.2em] font-bold font-body mb-3">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(legalFooterLabels.legal)}
          </span>
          <h1 className="font-display text-3xl md:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-4">
            {t(doc.title)}
          </h1>
          <p className="font-body text-[#0B1F3B]/50 text-sm mb-12">
            {t(legalFooterLabels.lastUpdated)}: {LAST_UPDATED}
          </p>

          <p className="font-body text-[#0B1F3B]/80 text-base md:text-lg leading-relaxed mb-12">
            {t(doc.intro)}
          </p>

          <div className="space-y-10">
            {doc.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-display text-xl md:text-2xl text-[#0B1F3B] font-semibold mb-4">
                  {t(section.heading)}
                </h2>
                <p className="font-body text-[#0B1F3B]/75 text-base leading-relaxed whitespace-pre-line">
                  {t(section.body)}
                </p>
              </section>
            ))}
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-[#0B1F3B]/10 py-8 mt-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="font-body text-[#0B1F3B]/50 text-sm">
            © {new Date().getFullYear()} Industria de Reuniones · Incubatour
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
